import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { TITLE_OPTIONS, DEPARTMENT_OPTIONS, LOGIN_TYPE_OPTIONS } from './types';
import type { SupplierFormData } from './types';

interface WizardStep3ProfileProps {
  formData: SupplierFormData;
  setFormData: React.Dispatch<React.SetStateAction<SupplierFormData>>;
  onInputChange: (field: keyof SupplierFormData, value: string | number | boolean | null | string[] | number[]) => void;
  previewMode: boolean;
}

export default function WizardStep3Profile({
  formData,
  setFormData,
  onInputChange,
  previewMode,
}: WizardStep3ProfileProps) {
  return (
    <div className="space-y-6">
      <div>
        {/* CR-3948: Updated heading */}
        <h2 className="text-xl font-semibold mb-2">Add New Contacts</h2>
        <p className="text-muted-foreground">
          Add contacts for this supplier. Fields marked with <span className="text-red-500">*</span> are mandatory.
        </p>
      </div>

      {/* Primary Contact Card */}
      <div className="border rounded-lg border-l-4 border-l-blue-600 bg-gray-50/50 p-5 space-y-5">
        <div className="flex items-center gap-2 mb-1">
          <Badge variant="default" className="bg-blue-600 text-white">Primary Contact</Badge>
          <span className="text-sm text-muted-foreground">This contact will be responsible for completing forms</span>
        </div>

        {/* Row 1: Title, First Name, Last Name */}
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="contact_title" className="text-sm font-semibold text-gray-700">Title</Label>
            <Select
              value={formData.contact_title || undefined}
              onValueChange={(v) => onInputChange('contact_title', v)}
              disabled={previewMode}
            >
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Select title..." />
              </SelectTrigger>
              <SelectContent>
                {TITLE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact_first_name" className="text-sm font-semibold text-gray-700">First Name <span className="text-red-500">*</span></Label>
            <Input
              id="contact_first_name"
              value={formData.contact_first_name}
              onChange={(e) => onInputChange('contact_first_name', e.target.value)}
              placeholder="Enter first name"
              disabled={previewMode}
              required
              className="bg-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact_last_name" className="text-sm font-semibold text-gray-700">Last Name <span className="text-red-500">*</span></Label>
            <Input
              id="contact_last_name"
              value={formData.contact_last_name}
              onChange={(e) => onInputChange('contact_last_name', e.target.value)}
              placeholder="Enter last name"
              disabled={previewMode}
              required
              className="bg-white"
            />
          </div>
        </div>

        {/* Row 2: Email, Position */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="contact_email" className="text-sm font-semibold text-gray-700">Email <span className="text-red-500">*</span></Label>
            <Input
              id="contact_email"
              type="email"
              value={formData.contact_email}
              onChange={(e) => onInputChange('contact_email', e.target.value)}
              placeholder="Enter email"
              disabled={previewMode}
              required
              className="bg-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact_job_title" className="text-sm font-semibold text-gray-700">Position</Label>
            <Input
              id="contact_job_title"
              value={formData.contact_job_title}
              onChange={(e) => onInputChange('contact_job_title', e.target.value)}
              placeholder="Enter position"
              disabled={previewMode}
              className="bg-white"
            />
          </div>
        </div>

        {/* Row 3: Department, Telephone + Extension */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="contact_department" className="text-sm font-semibold text-gray-700">Department</Label>
            <Select
              value={formData.contact_department || undefined}
              onValueChange={(v) => onInputChange('contact_department', v)}
              disabled={previewMode}
            >
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Select department..." />
              </SelectTrigger>
              <SelectContent>
                {DEPARTMENT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact_phone" className="text-sm font-semibold text-gray-700">Telephone</Label>
            <div className="flex gap-2">
              <Input
                id="contact_phone"
                value={formData.contact_phone}
                onChange={(e) => onInputChange('contact_phone', e.target.value)}
                placeholder="Enter telephone"
                disabled={previewMode}
                className="flex-1 bg-white"
              />
              <Input
                id="contact_phone_extension"
                value={formData.contact_phone_extension}
                onChange={(e) => onInputChange('contact_phone_extension', e.target.value)}
                placeholder="Ext"
                disabled={previewMode}
                className="w-24 bg-white"
              />
            </div>
          </div>
        </div>

        {/* Row 4: LinkedIn, Login Type */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="contact_linkedin" className="text-sm font-semibold text-gray-700">LinkedIn</Label>
            <Input
              id="contact_linkedin"
              value={formData.contact_linkedin}
              onChange={(e) => onInputChange('contact_linkedin', e.target.value)}
              placeholder="Enter LinkedIn URL"
              disabled={previewMode}
              className="bg-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact_login_type" className="text-sm font-semibold text-gray-700">Default Login Type <span className="text-red-500">*</span></Label>
            <Select
              value={formData.contact_login_type}
              onValueChange={(v) => onInputChange('contact_login_type', v)}
              disabled={previewMode}
            >
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Select login type..." />
              </SelectTrigger>
              <SelectContent>
                {LOGIN_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* CR-3949: Additional Contacts */}
      {/* Additional Contacts */}
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium">Additional Contacts</h3>
            <p className="text-sm text-muted-foreground">Add more contacts for this supplier. They will automatically be linked to <span className="font-medium">{formData.company_name || 'the supplier company'}</span>.</p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => setFormData(prev => ({
              ...prev,
              additional_contacts: [...prev.additional_contacts, {
                title: '', first_name: '', last_name: '', email: '', phone: '',
                phone_extension: '', job_title: '', department: '', login_type: 'supplier',
                linkedin: '', is_primary: false,
              }],
            }))}
            disabled={previewMode}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Additional Contact
          </Button>
        </div>

        {formData.additional_contacts.map((contact, index) => (
          <div key={index} className="border rounded-lg border-l-4 border-l-gray-400 bg-gray-50/50 p-5">
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-gray-700">Contact {index + 2}</h4>
                  <Badge variant="outline" className="text-xs border-green-300 text-green-700 bg-green-50">Linked to {formData.company_name || 'supplier'}</Badge>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      additional_contacts: prev.additional_contacts.filter((_, i) => i !== index),
                    }));
                  }}
                  disabled={previewMode}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-700">Title</Label>
                  <Select
                    value={contact.title || undefined}
                    onValueChange={(v) => {
                      const updated = [...formData.additional_contacts];
                      updated[index] = { ...updated[index], title: v };
                      setFormData(prev => ({ ...prev, additional_contacts: updated }));
                    }}
                    disabled={previewMode}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Title..." />
                    </SelectTrigger>
                    <SelectContent>
                      {TITLE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-700">First Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={contact.first_name}
                    onChange={(e) => {
                      const updated = [...formData.additional_contacts];
                      updated[index] = { ...updated[index], first_name: e.target.value };
                      setFormData(prev => ({ ...prev, additional_contacts: updated }));
                    }}
                    placeholder="First name"
                    disabled={previewMode}
                    className="bg-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-700">Last Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={contact.last_name}
                    onChange={(e) => {
                      const updated = [...formData.additional_contacts];
                      updated[index] = { ...updated[index], last_name: e.target.value };
                      setFormData(prev => ({ ...prev, additional_contacts: updated }));
                    }}
                    placeholder="Last name"
                    disabled={previewMode}
                    className="bg-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-700">Email <span className="text-red-500">*</span></Label>
                  <Input
                    type="email"
                    value={contact.email}
                    onChange={(e) => {
                      const updated = [...formData.additional_contacts];
                      updated[index] = { ...updated[index], email: e.target.value };
                      setFormData(prev => ({ ...prev, additional_contacts: updated }));
                    }}
                    placeholder="Email"
                    disabled={previewMode}
                    className="bg-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-700">Position</Label>
                  <Input
                    value={contact.job_title}
                    onChange={(e) => {
                      const updated = [...formData.additional_contacts];
                      updated[index] = { ...updated[index], job_title: e.target.value };
                      setFormData(prev => ({ ...prev, additional_contacts: updated }));
                    }}
                    placeholder="Position"
                    disabled={previewMode}
                    className="bg-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-700">Phone</Label>
                  <div className="flex gap-2">
                    <Input
                      value={contact.phone}
                      onChange={(e) => {
                        const updated = [...formData.additional_contacts];
                        updated[index] = { ...updated[index], phone: e.target.value };
                        setFormData(prev => ({ ...prev, additional_contacts: updated }));
                      }}
                      placeholder="Phone"
                      disabled={previewMode}
                      className="flex-1 bg-white"
                    />
                    <Input
                      value={contact.phone_extension}
                      onChange={(e) => {
                        const updated = [...formData.additional_contacts];
                        updated[index] = { ...updated[index], phone_extension: e.target.value };
                        setFormData(prev => ({ ...prev, additional_contacts: updated }));
                      }}
                      placeholder="Ext"
                      disabled={previewMode}
                      className="w-20 bg-white"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-700">Department</Label>
                  <Select
                    value={contact.department || undefined}
                    onValueChange={(v) => {
                      const updated = [...formData.additional_contacts];
                      updated[index] = { ...updated[index], department: v };
                      setFormData(prev => ({ ...prev, additional_contacts: updated }));
                    }}
                    disabled={previewMode}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPARTMENT_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-700">Login Type <span className="text-red-500">*</span></Label>
                  <Select
                    value={contact.login_type}
                    onValueChange={(v) => {
                      const updated = [...formData.additional_contacts];
                      updated[index] = { ...updated[index], login_type: v };
                      setFormData(prev => ({ ...prev, additional_contacts: updated }));
                    }}
                    disabled={previewMode}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {LOGIN_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-700">LinkedIn</Label>
                  <Input
                    value={contact.linkedin}
                    onChange={(e) => {
                      const updated = [...formData.additional_contacts];
                      updated[index] = { ...updated[index], linkedin: e.target.value };
                      setFormData(prev => ({ ...prev, additional_contacts: updated }));
                    }}
                    placeholder="LinkedIn URL"
                    disabled={previewMode}
                    className="bg-white"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
